import { useState, useCallback } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import AskClaudePage from './pages/AskClaudePage'
import AccountsPage from './pages/AccountsPage'
import UserQuestionsGeminiPage from './pages/UserQuestionsGeminiPage'
import ConversationPage from './pages/ConversationPage'
import DataCorrectionPage from './pages/DataCorrectionPage'
import ProfilePage from './pages/ProfilePage'
import AccountSettingsPage from './pages/AccountSettingsPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import CookiePolicyPage from './pages/CookiePolicyPage'
import SupportPage from './pages/SupportPage'
import LandingPage from './pages/LandingPage'
import ConversationSidebar from './components/ConversationSidebar'
import { AuthProvider, useAuthContext } from './context/AuthContext'
import { ProgressionProvider, useProgressionContext } from './context/ProgressionContext'
import { NewChatProvider, useNewChatContext } from './context/NewChatContext'
import Icon, { type IconName } from './components/icons/Icon'
import './styles/app.css'
import './styles/progression.css'

/**
 * Main application component with routing and progression flow.
 *
 * @returns JSX.Element The root App component
 */
function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ProgressionProvider>
          <NewChatProvider>
            <AppShell />
          </NewChatProvider>
        </ProgressionProvider>
      </Router>
    </AuthProvider>
  )
}

function AppShell() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const { user, isAuthenticated, logout } = useAuthContext()
  const navigate = useNavigate()
  const location = useLocation()
  const { requestNewChat } = useNewChatContext()

  const displayName = user?.first_name || user?.email || 'User'
  const userEmail = user?.email || ''

  const NAV_LINKS = [
    { label: 'Home', to: '/' },
    { label: 'Accounts', to: '/accounts' },
    { label: 'Ask Claude', to: '/askclaude' },
    { label: 'Gemini Live', to: '/userquestionsgemini' },
  ]

  const handleSelectConversation = useCallback((conversationId: number) => {
    navigate(`/askclaude?conversation=${conversationId}`)
  }, [navigate])

  const handleNewConversation = useCallback(() => {
    requestNewChat()
    if (location.pathname !== '/askclaude') {
      navigate('/askclaude')
    }
  }, [navigate, location.pathname, requestNewChat])

  // Signed-out visitors on "/" get the full-bleed marketing landing page;
  // it manages its own layout, so skip the app container's width/padding.
  const isLandingView = location.pathname === '/' && !isAuthenticated

  return (
          <div className={`app-wrapper ${isAuthenticated ? (isSidebarCollapsed ? 'sidebar-collapsed' : '') : 'no-sidebar'}`}>
            {/* Global sidebar — available on all pages, nav + conversations + user */}
            {isAuthenticated && (
              <ConversationSidebar
                activeConversationId={null}
                onSelectConversation={handleSelectConversation}
                onNewConversation={handleNewConversation}
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
                navigationLinks={NAV_LINKS}
                userName={displayName}
                userEmail={userEmail}
                onLogout={() => { void logout() }}
              />
            )}

            {/* Mobile burger — hidden on desktop via CSS */}
            {isAuthenticated && (
              <button
                className="mobile-nav-btn"
                onClick={() => setIsSidebarOpen(true)}
                title="Menu"
              >
                &#9776;
              </button>
            )}

            {/* Main Content */}
            <main className={isLandingView ? 'landing-main' : 'container'}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                {/* Flow routes */}
                <Route path="/accounts" element={<ProtectedRoute><AccountsPage /></ProtectedRoute>} />
                {/* Plaid OAuth redirect lands here when a bank uses the dedicated path
                    instead of /accounts; forward to /accounts preserving query params
                    so the AccountsPage resumption logic can pick it up. */}
                <Route path="/plaid/oauth-redirect" element={<PlaidOAuthRedirect />} />
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                {/* Legacy LLM-driven assessment path. Hidden from primary
                    navigation but kept reachable by direct URL so voice
                    testing, demos, and any deep links keep working. */}
                <Route path="/conversation" element={<ProtectedRoute><ConversationPage /></ProtectedRoute>} />
                <Route path="/datacorrection" element={<DataCorrectionPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/cookies" element={<CookiePolicyPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/account" element={<AccountSettingsPage />} />
                <Route path="/askclaude" element={<ProtectedRoute><AskClaudePage /></ProtectedRoute>} />
                <Route path="/userquestionsgemini" element={<ProtectedRoute><UserQuestionsGeminiPage /></ProtectedRoute>} />
              </Routes>
            </main>

            {/* Footer */}
            <footer className="chat-footer">
              <div className="footer-links">
                <Link to="/privacy">Privacy Policy</Link>
                <span className="footer-sep" aria-hidden="true">|</span>
                <Link to="/terms">Terms of Service</Link>
                <span className="footer-sep" aria-hidden="true">|</span>
                <Link to="/cookies">Cookie Policy</Link>
                <span className="footer-sep" aria-hidden="true">|</span>
                <Link to="/support">Support</Link>
              </div>
              <p>&copy; 2024 Vault. All rights reserved.</p>
            </footer>
          </div>
  )
}

function PlaidOAuthRedirect() {
  const location = useLocation()
  return <Navigate to={{ pathname: '/accounts', search: location.search }} replace />
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, beginGoogleLogin } = useAuthContext()

  if (isLoading) {
    return <div className="user-greeting">Checking session…</div>
  }

  if (!isAuthenticated) {
    return (
      <div className="home-page">
        <h1 className="home-title">Sign in required</h1>
        <p className="home-subtitle">
          AI chat, voice, and account-linking features require an authenticated session.
        </p>
        <button
          onClick={() => { void beginGoogleLogin(window.location.href) }}
          className="btn btn-primary"
        >
          Sign in with Google
        </button>
      </div>
    )
  }

  return <>{children}</>
}

/**
 * Home page component with flow entry point.
 *
 * @returns JSX.Element The home page
 */
const CARD_META: Record<string, { icon: IconName; description: string }> = {
  accounts: { icon: 'vault', description: 'Link banks and brokerages through Plaid, read-only' },
  profile: { icon: 'clipboard', description: 'Fill in the details Plaid can\u2019t see \u2014 goals, dependents, insurance' },
  geminilive: { icon: 'waveform', description: 'Talk it through out loud, hands-free' },
  askclaude: { icon: 'sparkle', description: 'Ask anything about your money and get a reasoned answer' },
}

function HomePage() {
  const { visibleSteps } = useProgressionContext()
  const { isAuthenticated, user } = useAuthContext()

  // Signed-out visitors see the marketing landing page; the step grid below
  // is the post-sign-in home. (While the session check is in flight we show
  // the landing too, matching the previous signed-out-first behavior.)
  if (!isAuthenticated) {
    return <LandingPage />
  }

  return (
    <div className="home-page">
      <h1 className="home-title">
        {user?.first_name ? `Welcome back, ${user.first_name}.` : 'Welcome back.'}
      </h1>
      <p className="home-subtitle">
        Connect your accounts, fill in what they can&rsquo;t see, then talk it
        through &mdash; by chat or live voice.
      </p>

      <div className="features-grid">
        {visibleSteps.map((step) => {
          const meta = CARD_META[step.id]
          return (
            <FeatureCard
              key={step.id}
              icon={meta.icon}
              title={step.label}
              description={meta.description}
              link={step.route}
            />
          )
        })}
      </div>
    </div>
  )
}

/**
 * Feature card component for the home page.
 *
 * @param props - Component props
 * @returns JSX.Element The feature card
 */
function FeatureCard({ icon, title, description, link }: {
  icon: IconName
  title: string
  description: string
  link: string
}) {
  return (
    <Link to={link} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="feature-card">
        <span className="feature-icon"><Icon name={icon} /></span>
        <h3 className="feature-title">{title}</h3>
        <p className="feature-desc">{description}</p>
      </div>
    </Link>
  )
}

export default App
